// eslint-disable-next-line no-restricted-imports
import {
  OrderDetailsForAdminDto,
  OrderFragment,
  Query_OrderController_GetById_OwnerType,
} from 'api/generated/graphql';
import {
  OrderDetailsEntity,
  OrderEntity,
  OrderMarketType,
  OrderSettlementConditionType,
  OrderStatusHistoryEntity,
  OrderTransactionVenueType,
  OrderType,
} from 'api/types/entity';
import { OperationStatusType } from 'api/types/entity/operation';
import {
  OrderExecutionTermsOperationKind,
  OrderExecutionTermsOrderType,
} from 'api/types/entity/order/orderExecutionTermsEntity';
import { OrderOperationType } from 'api/types/entity/order/orderOperationType';
import { OrderStatusType } from 'api/types/entity/order/orderStatusType';
import { lodash } from 'helpers';

import { parseFileDocumentEntity } from '../client/parseEntity/parseFileEntity';
import { parseManagerData } from '../parseManagerData';

export const orderTypeMap: Record<OrderOperationType, OrderType> = {
  [OrderOperationType.SecuritiesPurchase]: OrderType.Order,
  [OrderOperationType.SecuritiesSale]: OrderType.Order,
  [OrderOperationType.SecuritiesWithdrawal]: OrderType.Decree,
  [OrderOperationType.SecuritiesDeposit]: OrderType.Decree,
};

const parseDetails = (
  details: OrderDetailsForAdminDto
): OrderDetailsEntity => ({
  accountNumber: details.accountNumber,
  accountOwnerName: details.accountOwnerName ?? undefined,
  brokerName: {
    value: details.brokerName.value,
    isNew: details.brokerName.isNew ?? undefined,
    referenceRecordId: details.brokerName.referenceRecordId ?? undefined,
  },
  custodianOrganization: {
    value: details.custodianOrganization.value,
    isNew: details.custodianOrganization.isNew ?? undefined,
    referenceRecordId:
      details.custodianOrganization.referenceRecordId ?? undefined,
  },
  custodianOrganizationCode: details.custodianOrganizationCode ?? undefined,
});

export const parseOrderEntity = (data: OrderFragment): OrderEntity => ({
  id: data.id,
  owner: {
    id: data.ownerId,
    fullName: data.ownerFullName ?? undefined,
    accountNumber: data.ownerPersonalAccountNumber ?? undefined,
    type:
      data.ownerType === Query_OrderController_GetById_OwnerType.Client
        ? 'person'
        : 'legal',
  },
  hasBrokerRecommendation: data.hasBrokerRecommendation,
  marketType: data.marketType ? OrderMarketType[data.marketType] : undefined,
  fmNumber: data.fmNumber ?? undefined,
  transactionVenue: data.transactionVenue
    ? OrderTransactionVenueType[data.transactionVenue]
    : undefined,
  settlementCondition: data.settlementCondition
    ? OrderSettlementConditionType[data.settlementCondition]
    : undefined,
  status: OrderStatusType[data.status],
  number: data.number ?? undefined,
  createdAt: new Date(data.createdAt),
  operationType: OrderOperationType[data.operationType],
  orderType: orderTypeMap[data.operationType],
  startDate: data.startDate ? new Date(data.startDate) : undefined,
  endDate: data.endDate ? new Date(data.endDate) : undefined,
  signedAt: data.signedAt ? new Date(data.signedAt) : undefined,
  closedDate: data.closedDate ? new Date(data.closedDate) : undefined,

  executionPeriodInBusinessDays:
    data.executionPeriodInBusinessDays ?? undefined,

  transactionVolume: data.transactionVolume
    ? {
        quantity: data.transactionVolume.quantity,
        unitPrice: data.transactionVolume.unitPrice ?? undefined,
        transactionAmount:
          data.transactionVolume.transactionAmount ?? undefined,
        currency: data.transactionVolume.currency,
      }
    : undefined,

  transferVolume: data.transferVolume
    ? {
        quantity: data.transferVolume.quantity,
        unitPrice: data.transferVolume.unitPrice ?? undefined,
        transactionAmount: data.transferVolume.transactionAmount ?? undefined,
        currency: data.transferVolume.currency ?? undefined,
      }
    : undefined,
  financialInstrument: {
    nominalValue: data.financialInstrument.nominalValue,
    isin: {
      value: data.financialInstrument.isin.value,
      isNew: data.financialInstrument.isin.isNew ?? undefined,
      referenceRecordId:
        data.financialInstrument.isin.referenceRecordId ?? undefined,
    },
    securityType: data.financialInstrument.securityType ?? undefined,
    issuer: data.financialInstrument.issuer ?? undefined,
  },
  executionTerms: {
    additionalNotes: data.executionTerms.additionalNotes ?? undefined,
    orderType: data.executionTerms.orderType
      ? OrderExecutionTermsOrderType[data.executionTerms.orderType]
      : undefined,
    yield: data.executionTerms.yield ?? undefined,
    operationKind: data.executionTerms.operationKind
      ? OrderExecutionTermsOperationKind[data.executionTerms.operationKind]
      : undefined,
    operationPurpose: data.executionTerms.operationPurpose ?? undefined,
  },

  senderDetails: data.senderDetails
    ? parseDetails(data.senderDetails)
    : undefined,

  recipientDetails: data.recipientDetails
    ? parseDetails(data.recipientDetails)
    : undefined,

  additionalParameters: data.additionalParameters
    ? {
        settlementDate: data.additionalParameters.settlementDate
          ? new Date(data.additionalParameters.settlementDate)
          : undefined,
        transactionDate: data.additionalParameters.transactionDate
          ? new Date(data.additionalParameters.transactionDate)
          : undefined,
      }
    : undefined,

  confirmationDocument: data.confirmationDocument
    ? {
        contractDate: data.confirmationDocument.contractDate
          ? new Date(data.confirmationDocument.contractDate)
          : undefined,
        contractNumber: data.confirmationDocument.contractNumber ?? undefined,
        files: lodash
          .compact(data.confirmationDocument.documents)
          .map(parseFileDocumentEntity),
      }
    : undefined,

  manager: data.assignedEmployee
    ? parseManagerData(data.assignedEmployee)
    : undefined,

  comment: data.comment ?? undefined,

  adminDocuments: lodash
    .compact(data.adminDocuments)
    .map(parseFileDocumentEntity),

  versionNumber: data.version?.number,

  statusesHistory: lodash
    .compact(data.statuses)
    .filter((status) => status.createdBy)
    .map(
      (status): OrderStatusHistoryEntity => ({
        id: status.id,
        value: OrderStatusType[status.value],
        createdAt: new Date(status.createdAt),
        employee: parseManagerData(status.createdBy!),
        fmNumber: status.fmNumber ?? undefined,
        reason: status.reason ?? undefined,
        closedDate: status.closedDate ? new Date(status.closedDate) : undefined,
      })
    ),

  executedQuantity: data.executedQuantity ?? undefined,
  requestedQuantity: data.requestedQuantity ?? undefined,
  operations: lodash.compact(data.operations).map((operation) => ({
    id: operation.id,
    number: operation.number,
    status: OperationStatusType[operation.status],
    quantity: operation.quantity,
    executedAt: new Date(operation.executedAt),
    netUnitPrice: operation.netUnitPrice,
    isin: data.financialInstrument.isin.value,
  })),
});
